import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useAuth } from './auth/AuthContext';
import { usePeriod } from './hooks/usePeriod';
import { useTourDemo } from './lib/tourDemo';

import Home         from './pages/Home';
import SectionPage  from './pages/SectionPage';
import AdminPage    from './pages/AdminPage';
import PeriodClosed from './pages/PeriodClosed';
import PhaseLocked  from './pages/PhaseLocked';
import SteeringPage from './pages/SteeringPage';
import BillingApprovalsPage from './pages/BillingApprovalsPage';
import NavBot       from './components/NavBot';
import { HeartbeatProvider } from './hooks/useHeartbeat';

// A section is reachable only when its route period is one of the caller's
// applicable active periods (usePeriod resolves the :period route param).
function SectionRoute() {
  const { periodActive, sections } = usePeriod();
  const { sectionKey } = useParams();
  const { configError } = useAuth();
  const demo = useTourDemo();
  // During the spotlight tour, always render the section — it serves curated
  // demo data and must show the page it's walking through even if the route
  // period isn't an active cycle (e.g. the synthetic '/section/DEMO/...').
  // A section whose phase hasn't released yet can't be opened (an admin can, even while impersonating).
  const sec = (sections || []).find(x => x.sectionKey === sectionKey);
  if (periodActive && !demo && sec?.phase?.lockedForMe) {
    return <PhaseLocked label={sec.label} message={`${sec.phase.message}.`} />;
  }
  if (periodActive || demo) return <SectionPage />;
  // diConfig failed after retries (transient server error). Show a reload
  // prompt rather than PeriodClosed, which implies the period is inactive.
  if (configError) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 'calc(100vh - 52px)', gap: 12 }}>
      <i className="fas fa-exclamation-triangle" style={{ fontSize: '3rem', color: '#b45309' }} />
      <h2 style={{ margin: 0, color: '#b45309' }}>Connection Error</h2>
      <p style={{ color: '#555', margin: 0 }}>Could not load your session. Please refresh the page.</p>
      <button onClick={() => window.location.reload()} style={{ marginTop: 8, padding: '8px 20px', cursor: 'pointer' }}>
        Refresh
      </button>
    </div>
  );
  return <PeriodClosed />;
}

function AppRoutes() {
  const { ready, email, isAdmin, isSteering, isBillingTeam, periods } = useAuth();

  if (!ready) return <div className="app-loading">Loading...</div>;
  if (!email) {
    window.location.replace('/login');
    return <div className="app-loading">Redirecting to login...</div>;
  }

  // Section routes are period-scoped (/section/:period/:sectionKey). When the
  // caller is in exactly one active cycle we can hand the navbot that period so
  // it can build clickable section deeplinks; with zero or multiple cycles it's
  // ambiguous, so we pass null and the navbot points to the Home dashboard.
  const navbotPeriod = (periods && periods.length === 1) ? periods[0].period : null;

  return (
    <HeartbeatProvider>
      <Routes>
        <Route path="/"                            element={<Home />} />
        <Route path="/section/:period/:sectionKey" element={<SectionRoute />} />
        <Route path="/admin"                       element={
          isAdmin ? <AdminPage /> : <Navigate to="/" replace />
        } />
        <Route path="/steering"                    element={
          (isSteering || isAdmin) ? <SteeringPage /> : <Navigate to="/" replace />
        } />
        <Route path="/billing-approvals"           element={
          isBillingTeam ? <BillingApprovalsPage /> : <Navigate to="/" replace />
        } />
        <Route path="*"                            element={<Navigate to="/" replace />} />
      </Routes>
      {/* KB Navigator — floating help widget, rides along on every page.
          isSuperUser maps to this app's admin allowlist flag; period lets it
          build period-scoped /section/:period/:key deeplinks when unambiguous. */}
      <NavBot userId={email} userName={email} isSuperUser={isAdmin} period={navbotPeriod} />
    </HeartbeatProvider>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppRoutes />
      <ToastContainer
        position="top-right"
        autoClose={4000}
        newestOnTop
        pauseOnFocusLoss={false}
        theme="light"
      />
    </BrowserRouter>
  );
}
